import { useEffect, lazy, Suspense } from 'react';
import { Routes, Route, useLocation } from 'react-router-dom';
import { LastLocationProvider } from 'react-router-dom-last-location';

import { useUsers } from 'hooks';
import { appRoutes } from 'routes';
import { hasAttorneyAccess, hasMarketeerAccess, hasAnyAdminAccess } from 'domain/user/service';
import Modal from 'components/Modal';

const AdminRouter = lazy(() => import('./components/AdminRouter'));
const AttorneyRouter = lazy(() => import('./components/AttorneyRouter'));
const MarketeerRouter = lazy(() => import('./components/MarketeerRouter'));
const PublicRouter = lazy(() => import('./components/PublicRouter'));

const getClientIdFromPath = (pathname: string) => {
  // Get the clientId and pass it to the current_user endpoint when an admin accesses a client's MBD report
  const match = pathname.match(/\/clients\/([^/]+)\/(mbdReport|attorneyReport)(?:\/|$)/);
  return match?.[1];
};

const Router: React.FC = () => {
  const location = useLocation();
  const paramsClientId = getClientIdFromPath(location.pathname);
  const { loadCurrentUser, currentUser, isCurrentUserLoadFinished } = useUsers(paramsClientId);

  useEffect(() => {
    loadCurrentUser();
  }, [paramsClientId]);

  const options = [
    {
      key: 'attorney',
      access: currentUser && hasAttorneyAccess(currentUser),
      path: appRoutes.public.rootPath(),
      router: <AttorneyRouter />,
    },
    {
      key: 'marketeer',
      access: currentUser && hasMarketeerAccess(currentUser),
      path: appRoutes.public.rootPath(),
      router: <MarketeerRouter />,
    },
    {
      key: 'admin',
      access: currentUser && hasAnyAdminAccess(currentUser),
      path: appRoutes.public.rootPath(),
      router: <AdminRouter />,
    },
    {
      key: 'public',
      access: !currentUser,
      path: appRoutes.public.rootPath(),
      router: <PublicRouter />,
    },
  ];

  if (!isCurrentUserLoadFinished) return <Suspense fallback={null} />;

  return (
    <LastLocationProvider>
      <Suspense fallback={null}>
        <Routes>
          {options.map(option => {
            if (!option.access) return null;
            return <Route key={option.key} path={option.path} element={option.router} />;
          })}
        </Routes>
        <Modal />
      </Suspense>
    </LastLocationProvider>
  );
};

export default Router;
